import React, { useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { History, Download, Camera, RotateCcw, ShieldAlert, AlertTriangle, RefreshCw, ArrowUpRight, ArrowDownLeft, CheckCircle2 } from "lucide-react";

const eur = (n) => euro(n);
const dt = (s) => (s ? new Date(s).toLocaleString("it-IT") : "");

async function downloadBlob(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const href = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = href; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(href);
}

export default function AdminAudit() {
  const [tab, setTab] = useState("log");
  const [days, setDays] = useState(30);
  const [flow, setFlow] = useState("all");
  const [ledger, setLedger] = useState(null);
  const [snaps, setSnaps] = useState([]);
  const [retention, setRetention] = useState(30);
  const [busy, setBusy] = useState(false);

  const loadLedger = () => api.get(`/admin/audit/transactions?days=${days}&flow=${flow}`).then((r) => setLedger(r.data)).catch(() => {});
  const loadSnaps = () => api.get("/admin/audit/snapshots").then((r) => { setSnaps(r.data.snapshots || []); setRetention(r.data.retention_days || 30); }).catch(() => {});
  useEffect(() => { loadLedger(); }, [days, flow]);
  useEffect(() => { loadSnaps(); }, []);

  const takeSnapshot = async () => {
    setBusy(true);
    try { await api.post("/admin/audit/snapshots"); toast.success("Snapshot creato"); loadSnaps(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  // restore dialog
  const [restoreSnap, setRestoreSnap] = useState(null);
  const [restoreText, setRestoreText] = useState("");
  const doRestore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/audit/snapshots/${restoreSnap.id}/restore`, { confirm: restoreText });
      toast.success(`Ripristinati ${data.restored} saldi allo stato del ${dt(data.target)}`);
      setRestoreSnap(null); setRestoreText(""); loadSnaps(); loadLedger();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore ripristino"); }
    finally { setBusy(false); }
  };

  // reset dialog
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetPwd, setResetPwd] = useState("");
  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/audit/reset-credits", { confirm: resetText, delete_demo: false, password: resetPwd || undefined });
      toast.success(`Azzerati ${data.zeroed_accounts} conti. Backup salvato (${dt(data.backup_at)}).`);
      setResetOpen(false); setResetText(""); setResetPwd(""); loadSnaps(); loadLedger();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore azzeramento"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Amministrazione</p>
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><History className="w-7 h-7 text-primary" /> Log & Backup Crediti</h1>
          <p className="text-sm text-muted-foreground mt-1">Registro transazioni, snapshot firmati dei crediti (storico {retention} giorni), ripristino e azzeramento.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant={tab === "log" ? "default" : "outline"} size="sm" onClick={() => setTab("log")} data-testid="audit-tab-log">Log transazioni</Button>
        <Button variant={tab === "snap" ? "default" : "outline"} size="sm" onClick={() => setTab("snap")} data-testid="audit-tab-snap">Snapshot & Backup</Button>
      </div>

      {tab === "log" && (
        <Card className="glow-border p-5 space-y-4" data-testid="audit-ledger-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Label className="mb-0 text-sm">Periodo</Label>
              <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="audit-days-select">
                <option value={7}>7 giorni</option>
                <option value={30}>30 giorni</option>
              </select>
              <select value={flow} onChange={(e) => setFlow(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="audit-flow-select">
                <option value="all">Tutti i movimenti</option>
                <option value="system">Solo addebiti di sistema</option>
              </select>
              <Button variant="outline" size="sm" className="gap-1" onClick={loadLedger} data-testid="audit-ledger-refresh"><RefreshCw className="w-4 h-4" /> Aggiorna</Button>
            </div>
            <Button className="gap-2" onClick={() => downloadBlob(`/admin/audit/transactions/download?days=${days}&flow=${flow}`, `log-transazioni-${days}g.zip`)} data-testid="audit-ledger-download">
              <Download className="w-4 h-4" /> Scarica ZIP firmato
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="rounded-lg border border-border p-3"><p className="text-2xl font-head font-bold">{ledger?.count ?? 0}</p><p className="text-xs text-muted-foreground">Movimenti</p></div>
            <div className="rounded-lg border border-border p-3"><p className="text-2xl font-head font-bold text-teal-500">{eur(ledger?.total_in)}</p><p className="text-xs text-muted-foreground">Entrate</p></div>
            <div className="rounded-lg border border-border p-3"><p className="text-2xl font-head font-bold text-red-400">{eur(ledger?.total_out)}</p><p className="text-xs text-muted-foreground">Uscite</p></div>
          </div>

          <div className="rounded-lg border border-border divide-y divide-border max-h-[520px] overflow-auto" data-testid="audit-ledger-list">
            {(ledger?.items || []).map((it, i) => {
              const out = Number(it.amount) < 0;
              return (
                <div key={i} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" data-testid={`audit-txn-row-${i}`}>
                  <div className="min-w-0 flex items-center gap-2">
                    {out ? <ArrowUpRight className="w-4 h-4 text-red-400 shrink-0" /> : <ArrowDownLeft className="w-4 h-4 text-teal-500 shrink-0" />}
                    <div className="min-w-0">
                      <p className="truncate">{it.description || it.type}</p>
                      <p className="text-xs text-muted-foreground truncate">{it.owner_name || it.owner_email || "—"} · {it.owner_role || ""} · {dt(it.created_at)}</p>
                    </div>
                  </div>
                  <span className={`font-mono font-semibold shrink-0 ${out ? "text-red-400" : "text-teal-500"}`}>{out ? "−" : "+"} {eur(Math.abs(Number(it.amount)))}</span>
                </div>
              );
            })}
            {(!ledger || ledger.items.length === 0) && <p className="text-sm text-muted-foreground p-4">Nessun movimento nel periodo.</p>}
          </div>
        </Card>
      )}

      {tab === "snap" && (
        <>
          <Card className="glow-border p-5 space-y-4" data-testid="audit-snapshots-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-head text-xl font-bold flex items-center gap-2"><Camera className="w-5 h-5 text-primary" /> Snapshot crediti</h2>
                <p className="text-sm text-muted-foreground mt-0.5">Ogni snapshot fotografa i saldi di tutti i conti ed è firmato dal server (non manomettibile). Storico ultimi {retention} giorni.</p>
              </div>
              <Button className="gap-2" onClick={takeSnapshot} disabled={busy} data-testid="audit-snapshot-now"><Camera className="w-4 h-4" /> Crea snapshot ora</Button>
            </div>

            <div className="rounded-lg border border-border divide-y divide-border max-h-[520px] overflow-auto" data-testid="audit-snapshots-list">
              {snaps.map((s) => (
                <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm" data-testid={`audit-snap-row-${s.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-teal-500" /> {dt(s.created_at)}</p>
                    <p className="text-xs text-muted-foreground truncate">{s.trigger} · {s.count} conti · {eur(s.total_credit)} · firma {String(s.signature || "").slice(0, 10)}…</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" className="gap-1" onClick={() => downloadBlob(`/admin/audit/snapshots/${s.id}/download`, `snapshot-${s.id}.zip`)} data-testid={`audit-snap-download-${s.id}`}><Download className="w-4 h-4" /> ZIP</Button>
                    <Button variant="outline" size="sm" className="gap-1" onClick={() => { setRestoreText(""); setRestoreSnap(s); }} data-testid={`audit-snap-restore-${s.id}`}><RotateCcw className="w-4 h-4" /> Ripristina</Button>
                  </div>
                </div>
              ))}
              {snaps.length === 0 && <p className="text-sm text-muted-foreground p-4">Nessuno snapshot ancora. Creane uno con "Crea snapshot ora".</p>}
            </div>
          </Card>

          <Card className="p-5 space-y-4 border-destructive/40" data-testid="audit-reset-card">
            <div>
              <h2 className="font-head text-xl font-bold flex items-center gap-2 text-destructive"><ShieldAlert className="w-5 h-5" /> Azzera tutti i crediti</h2>
              <p className="text-sm text-muted-foreground mt-0.5">Porta a zero il saldo di <strong>tutti</strong> i conti (dealer, master, clienti, admin). Viene salvato automaticamente un backup ripristinabile prima dell'azzeramento.</p>
            </div>
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <span>Azione critica e globale. Prima di procedere verrà creato uno snapshot di backup così potrai tornare indietro dalla scheda "Snapshot & Backup".</span>
            </div>
            <Button variant="destructive" className="gap-2" onClick={() => { setResetText(""); setResetPwd(""); setResetOpen(true); }} data-testid="audit-reset-open">
              <ShieldAlert className="w-4 h-4" /> Azzera crediti
            </Button>
          </Card>
        </>
      )}

      {/* Restore dialog */}
      <Dialog open={!!restoreSnap} onOpenChange={(v) => !v && setRestoreSnap(null)}>
        <DialogContent data-testid="audit-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RotateCcw className="w-5 h-5 text-primary" /> Ripristina saldi</DialogTitle>
            <DialogDescription>Riporterai i saldi di tutti i conti allo stato del <strong>{dt(restoreSnap?.created_at)}</strong> ({restoreSnap?.count} conti, {eur(restoreSnap?.total_credit)}). Lo stato attuale verrà salvato come backup. Digita <strong>RIPRISTINA</strong> per confermare.</DialogDescription>
          </DialogHeader>
          <Input value={restoreText} onChange={(e) => setRestoreText(e.target.value)} placeholder="RIPRISTINA" autoFocus data-testid="audit-restore-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreSnap(null)} data-testid="audit-restore-cancel">Annulla</Button>
            <Button onClick={doRestore} disabled={busy || restoreText.trim().toUpperCase() !== "RIPRISTINA"} className="gap-1" data-testid="audit-restore-confirm"><RotateCcw className="w-4 h-4" />{busy ? "Ripristino…" : "Ripristina"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="audit-reset-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive"><ShieldAlert className="w-5 h-5" /> Conferma azzeramento crediti</DialogTitle>
            <DialogDescription>Azione critica: tutti i saldi verranno portati a zero. Un backup ripristinabile verrà creato automaticamente. Digita <strong>AZZERA CREDITI</strong> per procedere.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="AZZERA CREDITI" autoFocus data-testid="audit-reset-input" />
            <div>
              <Label className="text-xs text-muted-foreground">Password amministratore (opzionale, se impostata)</Label>
              <Input type="password" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="••••••••" className="mt-1" data-testid="audit-reset-password" />
            </div>
            <Link to="/app/manutenzione" className="block text-sm text-primary underline" data-testid="audit-safe-test-data">Per i dati di test usa l'anteprima selettiva in Manutenzione: non verranno cancellati con l'azzeramento.</Link>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} data-testid="audit-reset-cancel">Annulla</Button>
            <Button variant="destructive" onClick={doReset} disabled={busy || resetText.trim().toUpperCase() !== "AZZERA CREDITI"} className="gap-1" data-testid="audit-reset-confirm"><ShieldAlert className="w-4 h-4" />{busy ? "Azzeramento…" : "Azzera definitivamente"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
